import { Injectable, OnDestroy } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

/**
 * Singleton service that subscribes ONCE to OidcSecurityService.userData$ and caches
 * user rights as a Set<string> for O(1) lookup.
 *
 * Use this instead of injecting OidcSecurityService directly in directives/components
 * that only need to check permissions. Avoids creating N subscriptions for N directive
 * instances on a single page.
 */
@Injectable({ providedIn: 'root' })
export class UserPermissionService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly userSubject = new BehaviorSubject<any>(null);
  private rightsSet: Set<string> | null = null;
  private isSuperUser = false;

  /** Emits whenever user data changes (login/logout). Replays the last value on subscribe. */
  readonly currentUser$ = this.userSubject.asObservable();

  constructor(oidcSecurityService: OidcSecurityService) {
    oidcSecurityService.userData$.pipe(takeUntil(this.destroy$)).subscribe((userData: any) => {
      const user = userData?.userData ?? null;
      this.isSuperUser = user?.isSuperUser === 'true';
      this.rightsSet = null;

      if (user?.right) {
        const rights: string[] = Array.isArray(user.right) ? user.right : [user.right];
        this.rightsSet = new Set<string>(rights);
      }

      this.userSubject.next(user);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Synchronous O(1) permission check. Returns true if user has at least one of the given rights. */
  hasAnyRight(rights: number[]): boolean {
    if (this.isSuperUser) {
      return true;
    }
    if (!this.rightsSet || !rights?.length) {
      return false;
    }
    return rights.some(r => this.rightsSet.has(r.toString()));
  }

  /**
   * Returns an Observable<boolean> that emits whenever user data changes.
   * Accepts a single right or an array of rights (any match = true).
   *
   * Example:
   *   canManage$ = this.userPermissionService.hasRight$(AppRights.WartungsfensterVerwalten);
   *   canEdit$   = this.userPermissionService.hasRight$([AppRights.ImporteVerwalten, AppRights.ImportePruefen]);
   */
  hasRight$(rights: number | number[]): Observable<boolean> {
    const rightsArray = Array.isArray(rights) ? rights : [rights];
    return this.currentUser$.pipe(
      map(() => this.hasAnyRight(rightsArray))
    );
  }

  /** Current user snapshot (synchronous). */
  get currentUser(): any {
    return this.userSubject.getValue();
  }
}
